import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Rating,
  Chip,
  Divider,
  Avatar,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Breadcrumbs,
  Link,
  Stack,
  Alert,
  Skeleton,
  Tooltip,
} from '@mui/material';
import AddShoppingCart from '@mui/icons-material/AddShoppingCart';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import Favorite from '@mui/icons-material/Favorite';
import LocalShipping from '@mui/icons-material/LocalShipping';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import Store from '@mui/icons-material/Store';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Share from '@mui/icons-material/Share';
import OriginIcon from '@mui/icons-material/LocationOn';
import SustainabilityIcon from '@mui/icons-material/Grass';
import JourneyIcon from '@mui/icons-material/Timeline';
import DealIcon from '@mui/icons-material/Handshake';
import LockIcon from '@mui/icons-material/Lock';
import { Product } from '../data/mockMarketplace';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useProducts } from '../context/ProductContext';
import ProductCardEnhanced from '../components/market/ProductCardEnhanced';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ProductDetails: React.FC = () => {
  const params = useParams<{ id?: string; productId?: string }>();
  const id = params.id ?? params.productId ?? '';
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { getProductById, products } = useProducts();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);

  useEffect(() => {
    // Simulate API fetch
    setLoading(true);
    if (!id) return;

    setTimeout(() => {
      const foundProduct = getProductById(id);
      if (foundProduct) {
        setProduct(foundProduct);
        // Find related products (same category, excluding current)
        const related = products
          .filter(p => p.category === foundProduct.category && p.id !== foundProduct.id)
          .slice(0, 4);
        setRelatedProducts(related);
      }
      setLoading(false);
    }, 500);

    // Reset state when ID changes
    setTabValue(0);
    setQuantity(1);
    window.scrollTo(0, 0);
  }, [id, products]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddToCart = () => {
    if (product) {
      // Add quantity times
      for (let i = 0; i < quantity; i++) {
        addToCart(product);
      }
      // Could show a snackbar here
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={40} width="60%" />
            <Skeleton variant="rectangular" height={100} sx={{ my: 2 }} />
            <Skeleton variant="rectangular" height={50} width={200} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>Product not found</Typography>
        <Button variant="contained" onClick={() => navigate('/marketplace')}>
          Back to Marketplace
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          component="button"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Home
        </Link>
        <Link
          color="inherit"
          component="button"
          onClick={() => navigate('/marketplace')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Marketplace
        </Link>
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Product Image Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ bgcolor: 'grey.100', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
            <Box
              component="img"
              src={product.image}
              alt={product.name}
              sx={{
                width: '100%',
                height: 500,
                objectFit: 'cover',
              }}
            />
            {product.discountPercentage && (
              <Chip
                label={`-${product.discountPercentage}%`}
                color="error"
                sx={{ position: 'absolute', top: 16, left: 16, fontWeight: 'bold' }}
              />
            )}
            <IconButton
              sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'white', '&:hover': { bgcolor: 'white' } }}
              onClick={() => toggleWishlist(product)}
            >
              {isInWishlist(product.id) ? <Favorite color="error" /> : <FavoriteBorder />}
            </IconButton>
          </Paper>
        </Grid>

        {/* Product Info Section */}
        <Grid item xs={12} md={6}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Chip label={product.category} color="primary" variant="outlined" size="small" sx={{ mb: 2 }} />
              <IconButton size="small"><Share /></IconButton>
            </Box>

            <Typography variant="h3" fontWeight="bold" gutterBottom>
              {product.name}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Rating value={product.rating ?? 0} readOnly precision={0.5} />
                <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                  ({product.reviews ?? 0} reviews)
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" color={product.stock > 0 ? "success.main" : "error.main"}>
                {product.stock > 0 ? `In Stock (${product.stock} available)` : "Out of Stock"}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 4 }}>
              <Typography variant="h3" color="primary.main" fontWeight="bold">
                KSh {product.price.toLocaleString()}
              </Typography>
              {product.originalPrice && (
                <Typography variant="h5" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                  KSh {product.originalPrice.toLocaleString()}
                </Typography>
              )}
            </Box>

            <Typography variant="body1" paragraph color="text.secondary" sx={{ mb: 4 }}>
              {product.description}
            </Typography>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </Button>
                <Typography sx={{ px: 2, fontWeight: 'bold' }}>{quantity}</Typography>
                <Button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  disabled={quantity >= product.stock}
                >
                  +
                </Button>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddShoppingCart />}
                fullWidth
                onClick={handleAddToCart}
                disabled={product.stock === 0}
              >
                Add to Cart
              </Button>
            </Box>

            {/* Key Features */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocalShipping color="action" />
                  <Typography variant="body2">Nationwide Delivery</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <VerifiedUser color="action" />
                  <Typography variant="body2">Quality Guarantee</Typography>
                </Stack>
              </Grid>
            </Grid>

            {/* Seller Info Brief */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>{product.seller.name[0]}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {product.seller.name}
                    {product.seller.verified && (
                      <CheckCircle color="primary" sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Member since {new Date(product.seller.joinedDate).getFullYear()} • {product.seller.location}
                  </Typography>
                </Box>
                <Tooltip title="Direct contact is disabled for security. All deals are managed via our Secure Deal Room.">
                  <Chip 
                    icon={<LockIcon sx={{ '&&': { fontSize: 14 } }} />} 
                    label="Secure Trading" 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Store />}
                  onClick={() => navigate(`/marketplace/seller/${product.seller.id}`)}
                >
                  View Store
                </Button>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <Box sx={{ mt: 8 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Description" />
          <Tab label="Specifications" />
          <Tab label="Farm-to-Table" />
          <Tab label={`Reviews (${product.reviews ?? 0})`} />
          <Tab label="Seller Info" />
        </Tabs>

        <CustomTabPanel value={tabValue} index={0}>
          <Typography paragraph>{product.description}</Typography>
          <Typography variant="h6" gutterBottom>Key Features</Typography>
          <ul>
            {(product.features || []).map((feature, index) => (
              <li key={index}><Typography>{feature}</Typography></li>
            ))}
          </ul>
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container>
                  <Grid item xs={6}><Typography fontWeight="bold">Category</Typography></Grid>
                  <Grid item xs={6}><Typography color="text.secondary">{product.category}</Typography></Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container>
                  <Grid item xs={6}><Typography fontWeight="bold">Unit</Typography></Grid>
                  <Grid item xs={6}><Typography color="text.secondary">{product.unit}</Typography></Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container>
                  <Grid item xs={6}><Typography fontWeight="bold">Stock Status</Typography></Grid>
                  <Grid item xs={6}><Typography color="text.secondary">{product.stock > 0 ? 'In Stock' : 'Out of Stock'}</Typography></Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <JourneyIcon color="primary" /> Product Journey
            </Typography>
            <Box sx={{ mt: 4, position: 'relative' }}>
              {[
                { stage: 'Harvested', location: product.seller.location, date: 'Mar 10, 2026', icon: <SustainabilityIcon /> },
                { stage: 'Quality Checked', location: 'HUB Distribution Center', date: 'Mar 11, 2026', icon: <CheckCircle /> },
                { stage: 'Ready for Shipping', location: 'Global Logistics Hub', date: 'Mar 12, 2026', icon: <LocalShipping /> },
              ].map((step, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 3, mb: 4, position: 'relative' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: index === 0 ? 'success.main' : 'primary.main', width: 40, height: 40 }}>
                      {step.icon}
                    </Avatar>
                    {index < 2 && (
                      <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'divider', my: 1 }} />
                    )}
                  </Box>
                  <Box sx={{ pt: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight="bold">{step.stage}</Typography>
                    <Typography variant="body2" color="text.secondary">{step.location}</Typography>
                    <Typography variant="caption" color="text.secondary">{step.date}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            
            <Paper sx={{ mt: 2, p: 3, borderRadius: '16px', bgcolor: 'success.light', color: 'success.dark', display: 'flex', alignItems: 'center', gap: 2 }}>
              <SustainabilityIcon />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">Sustainability Verified</Typography>
                <Typography variant="body2">This product was grown using 100% organic methods, reducing carbon footprint by 40%.</Typography>
              </Box>
            </Paper>
          </Box>
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={3}>
          <Box sx={{ maxWidth: 600 }}>
            {/* Mock Reviews */}
            {[1, 2, 3].map((item) => (
              <Paper key={item} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>U{item}</Avatar>
                    <Typography variant="subtitle2">User {item}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">2 days ago</Typography>
                </Box>
                <Rating value={5 - (item % 2)} readOnly size="small" sx={{ mb: 1 }} />
                <Typography variant="body2">
                  Great product! Fresh and delivered on time. Will definitely order again from this farmer.
                </Typography>
              </Paper>
            ))}
            <Button variant="outlined" fullWidth>View All Reviews</Button>
          </Box>
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={3}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main' }}>
                  {product.seller.name[0]}
                </Avatar>
                <Typography variant="h6" gutterBottom>{product.seller.name}</Typography>
                <Rating value={product.seller.rating} readOnly precision={0.5} sx={{ mb: 2 }} />

                <Stack spacing={2} sx={{ textAlign: 'left', my: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Response Rate</Typography>
                    <Typography fontWeight="bold">{product.seller.responseRate}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Verified</Typography>
                    <Typography fontWeight="bold" color="success.main">{product.seller.verified ? 'Yes' : 'No'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Location</Typography>
                    <Typography fontWeight="bold">{product.seller.location}</Typography>
                  </Box>
                  <Button
                    fullWidth
                    size="large"
                    variant="outlined"
                    startIcon={<DealIcon />}
                    sx={{ mt: 2, py: 1.5, borderRadius: '12px' }}
                    onClick={() => {
                      // In a real app, this would initiate a deal in the database
                      alert('Bulk quote request sent to seller.');
                    }}
                  >
                    Request Bulk Quote (B2B)
                  </Button>
                </Stack>

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Store />}
                  onClick={() => navigate(`/marketplace/seller/${product.seller.id}`)}
                >
                  Visit Store Profile
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </CustomTabPanel>
      </Box>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <Box sx={{ mt: 8, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
            Related Products
          </Typography>
          <Grid container spacing={3}>
            {relatedProducts.map((relatedProduct) => (
              <Grid key={relatedProduct.id} item xs={12} sm={6} md={3}>
                <ProductCardEnhanced
                  product={relatedProduct}
                  onAddToCart={addToCart}
                  onToggleFavorite={() => { }}
                  isFavorite={false}
                  onQuickView={() => navigate(`/marketplace/product/${relatedProduct.id}`)} // Reuse quickview prop to navigate for now
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default ProductDetails;
